import React from 'react';
import PropTypes from 'prop-types';
import Formsy from 'formsy-react';
import { FormsySelect, FormsyText } from 'formsy-material-ui/lib';
import MenuItem from 'material-ui/MenuItem';
import { Typography, Box } from '@material-ui/core';
import FormClass from '../../../../components/Treatments/TreatmentSchedule/shared/FormClass';
import SpecificTimeDetails from '../../../../components/Treatments/TreatmentSchedule/SpecificTime/SpecificTimeDetails';
import { recurrenceTypes } from '../../../../settings/constants';
import globals from '../../../../globals/communityGlobals';
import _ from '../../../../utils/lodash';

import { textFieldStyle, dropDownStyle, common } from '../../../../theme/AllocateBaseTheme';
import KeyboardDateSelector from '../../../../components/common/KeyboardDateTimeSelectors/KeyboardDateSelector';
import KeyboardTimeSelector from '../../../../components/common/KeyboardDateTimeSelectors/KeyboardTimeSelector';
import { getErrorProps, combineDateTime, checkRequiredOnBlur } from '../../../../utils/keyboardSelectorFunctions';
import { extractDateOnly, isDateFromBeforeDateTo, isValidDate } from '../../../../utils/dates';

const errors = globals.getArea('error');
const locale = globals.getArea('nonPatientActivities');

const recurrenceTextMap = {
  [recurrenceTypes.DAILY]: locale.getMsg('days'),
  [recurrenceTypes.WEEKLY]: locale.getMsg('weeks'),
};

class NonPatientActivitySchedule extends FormClass {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      validationErrors: {},
    };
  }
  setValidationErrors = newValidationErrors => {
    this.setState({
      validationErrors: newValidationErrors,
    });
  };
  isSpecific = true;
  combineRefs = el => {
    this.props.innerRef(el);
    this.form = el;
  };
  calculateEndDateTime = (startTime, endTime) => {
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    return endDate < startDate ? endDate.setDate(endDate.getDate() + 1) : endDate;
  };
  noValidationErrors = () => {
    const { validationErrors } = this.state;
    const { dateFrom, dateTo, startTime, endTime } = this.props.dateTimeValues;
    return (
      !validationErrors.dateFrom &&
      !validationErrors.dateTo &&
      !validationErrors.startTime &&
      !validationErrors.endTime &&
      isValidDate(dateFrom, true) &&
      isValidDate(dateTo, true) &&
      isValidDate(startTime, true) &&
      isValidDate(endTime, true)
    );
  };
  allDatesAndTimesAreSet = () => {
    const { dateFrom, dateTo, startTime, endTime } = this.props.dateTimeValues;
    return dateFrom && dateTo && startTime && endTime;
  };
  formIsSubmittable = () => {
    const { dateFrom, dateTo } = this.props.dateTimeValues;
    return this.allDatesAndTimesAreSet() && this.noValidationErrors() && isDateFromBeforeDateTo(dateFrom, dateTo);
  };
  handleDateChange = (fieldName, date) => {
    const { setState, setValidity } = this.props;
    let errorMsg = null;

    if (!date) {
      errorMsg = errors.getMsg('requiredFieldValidationMessage');
    } else {
      date = extractDateOnly(date);

      if (fieldName === 'dateFrom' && !this.isAtLeastTodayOrUnmodified('dateFrom')({}, date)) {
        errorMsg = errors.getMsg('mustBeAtLeastToday', [locale.getMsg('dateFrom')]);
      }

      if (!isValidDate(date)) {
        errorMsg = null;
      }
    }

    this.setValidationErrors({ ...this.state.validationErrors, [fieldName]: errorMsg });
    setState({ [fieldName]: date }, () => {
      this.invokeOnChange();
      setValidity(this.formIsSubmittable())();
    });
  };
  handleTimeChange = (fieldName, time) => {
    const { setState, setValidity } = this.props;
    let errorMsg = null;

    if (!time) {
      errorMsg = errors.getMsg('requiredFieldValidationMessage');
    }
    if (!isValidDate(time)) {
      errorMsg = null;
    }

    this.setValidationErrors({ ...this.state.validationErrors, [fieldName]: errorMsg });
    setState({ [fieldName]: time }, () => {
      this.invokeOnChange();
      setValidity(this.formIsSubmittable())();
    });
  };
  invokeOnChange = () => {
    const { dateFrom, dateTo } = this.props.dateTimeValues;
    let { startTime, endTime } = this.props.dateTimeValues;
    const { recurrenceType } = this.state;
    if (dateFrom && startTime) {
      startTime = combineDateTime(dateFrom, startTime);
    }
    if (dateTo && endTime) {
      endTime = combineDateTime(dateTo, endTime);
    }
    this.handleChange({
      dateFrom,
      dateTo,
      startTime,
      endTime,
      recurrenceType: recurrenceType.selected,
    });
  };

  handleRecurrenceTypeChange = event => {
    const selectedValue = event.target.textContent;
    const { setState, setValidity } = this.props;
    setState({ recurrenceType: selectedValue }, () => {
      this.setState(
        prevState => ({
          recurrenceType: {
            ...prevState.recurrenceType,
            selected: selectedValue,
          },
        }),
        () => {
          this.invokeOnChange();
          setValidity(this.formIsSubmittable())();
        },
      );
    });
  };

  render() {
    const { values, readOnly, shouldAssignToday, defaultValues } = this.props;
    const { dateFrom, dateTo, startTime, endTime } = this.props.dateTimeValues;
    const { recurrenceType, recurEvery, duration, validationErrors } = this.state;
    const recurrenceTypeOptions = _.filter(recurrenceType.options, item => item.name !== recurrenceTypes.MONTHLY);

    return (
      <Formsy.Form defaultValues={defaultValues} ref={this.combineRefs}>
        <Box display="flex" p={1} flexDirection="column">
          <Box>
            <Typography variant="h6">{locale.getMsg('activitySchedule')}</Typography>
          </Box>
          <Box display="flex" flexDirection="row">
            <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
              <KeyboardDateSelector
                name="dateFrom"
                id="date-picker-inline"
                required
                maxDate={dateTo || undefined}
                maxDateMessage=""
                label={`${locale.getMsg('dateFrom')}`}
                value={dateFrom}
                onChange={date => this.handleDateChange('dateFrom', date)}
                onBlur={event =>
                  checkRequiredOnBlur(event.target.value, this.setValidationErrors, 'dateFrom', validationErrors)
                }
                disabled={shouldAssignToday || readOnly}
                errorProps={getErrorProps(validationErrors.dateFrom)}
              />
            </Box>
            <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
              <KeyboardDateSelector
                name="dateTo"
                id="date-picker-inline"
                required
                label={`${locale.getMsg('dateTo')}`}
                value={dateTo}
                onChange={date => this.handleDateChange('dateTo', date)}
                onBlur={event =>
                  checkRequiredOnBlur(event.target.value, this.setValidationErrors, 'dateTo', validationErrors)
                }
                disabled={readOnly}
                errorProps={getErrorProps(validationErrors.dateTo)}
                minDate={dateFrom || undefined}
                minDateMessage={errors.getMsg('cannotBeEarlier', [locale.getMsg('dateFrom')])}
              />
            </Box>
            <Box p={1} flexGrow={1} style={{ width: '16.5%' }}>
              <KeyboardTimeSelector
                name="startTime"
                id="start-time"
                required
                label={`${locale.getMsg('startTime')}`}
                value={startTime}
                onChange={time => this.handleTimeChange('startTime', time)}
                onBlur={event =>
                  checkRequiredOnBlur(event.target.value, this.setValidationErrors, 'startTime', validationErrors)
                }
                disabled={readOnly}
                errorProps={getErrorProps(validationErrors.startTime)}
              />
            </Box>
            <Box p={1} flexGrow={1} style={{ width: '16.5%' }}>
              <KeyboardTimeSelector
                name="endTime"
                id="end-time"
                required
                label={`${locale.getMsg('endTime')}`}
                value={endTime}
                onChange={time => this.handleTimeChange('endTime', time)}
                onBlur={event => checkRequiredOnBlur(event.target.value, this.setValidationErrors, 'endTime')}
                disabled={readOnly}
                errorProps={getErrorProps(validationErrors.endTime)}
              />
            </Box>
          </Box>
          <Box display="flex" flexDirection="row">
            <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
              <FormsyText
                name="duration"
                value={duration || duration === 0 ? duration.toString() : undefined}
                disabled
                floatingLabelText={`${locale.getMsg('duration')}`}
                className="col-c3 formsytext-css"
                floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                style={textFieldStyle.style}
                inputStyle={textFieldStyle.inputStyle}
                underlineStyle={textFieldStyle.underlineStyle}
                underlineFocusStyle={textFieldStyle.underlineFocusStyle}
              />
            </Box>
            <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
              <FormsySelect
                name="recurrenceType"
                floatingLabelText={locale.getMsg('recurrenceType')}
                value={recurrenceType.selected}
                disabled={recurrenceType.disabled || readOnly}
                className="col-c3"
                floatingLabelStyle={common.floatingLabelStyle}
                floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
                floatingLabelFocusStyle={common.floatingLabelFocusStyle}
                underlineStyle={common.underlineStyle}
                style={dropDownStyle.style}
                iconStyle={dropDownStyle.iconStyle}
                labelStyle={dropDownStyle.labelStyle}
                menuStyle={dropDownStyle.menuStyle}
                onChange={this.handleRecurrenceTypeChange}
              >
                {recurrenceTypeOptions.map(({ value, name }) => (
                  <MenuItem key={`recurrenceOption${value}`} value={value} primaryText={name} />
                ))}
              </FormsySelect>
            </Box>
            <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
              {!recurEvery.disabled && (
                <FormsySelect
                  name="recurEvery"
                  floatingLabelText={locale.getMsg('recurEvery')}
                  value={recurEvery.selected}
                  disabled={recurEvery.disabled || readOnly}
                  className="col-c3"
                  floatingLabelStyle={common.floatingLabelStyle}
                  floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
                  floatingLabelFocusStyle={common.floatingLabelFocusStyle}
                  underlineStyle={common.underlineStyle}
                  style={dropDownStyle.style}
                  iconStyle={dropDownStyle.iconStyle}
                  labelStyle={dropDownStyle.labelStyle}
                  menuStyle={dropDownStyle.menuStyle}
                >
                  {recurEvery.options.map(({ value }) => (
                    <MenuItem key={`recurEveryOption${value}`} value={value} primaryText={value} />
                  ))}
                </FormsySelect>
              )}
              <div className="extra-info">
                {!recurEvery.disabled && recurEvery.options.length ? recurrenceTextMap[recurrenceType.selected] : null}
              </div>
            </Box>
          </Box>
        </Box>

        <SpecificTimeDetails
          name="selectedDays"
          recurrenceType={recurrenceType.selected}
          required={recurrenceType.selected === recurrenceTypes.WEEKLY}
          value={values.selectedDays}
          disabled={readOnly}
        />
      </Formsy.Form>
    );
  }
}

NonPatientActivitySchedule.defaultProps = {
  innerRef: () => {},
  setValidity: () => {},
  defaultValues: {
    recurEvery: 1,
    recurrenceType: 'Daily',
    dateTo: undefined,
    dateFrom: undefined,
    startTime: undefined,
    endTime: undefined,
    duration: undefined,
    selectedDays: undefined,
  },
  readOnly: false,
  shouldAssignToday: false,
};

NonPatientActivitySchedule.propTypes = {
  defaultValues: PropTypes.shape({
    recurEvery: PropTypes.number,
    recurrenceType: PropTypes.string,
    selectedDays: PropTypes.objectOf(PropTypes.objectOf(PropTypes.bool)),
    dateTo: PropTypes.date,
    dateFrom: PropTypes.date,
    startTime: PropTypes.date,
    endTime: PropTypes.date,
    duration: PropTypes.string,
  }),
  values: PropTypes.shape({
    recurEvery: PropTypes.number,
    recurrenceType: PropTypes.string,
    selectedDays: PropTypes.objectOf(PropTypes.objectOf(PropTypes.bool)),
    dateTo: PropTypes.date,
    dateFrom: PropTypes.date,
    startTime: PropTypes.date,
    endTime: PropTypes.date,
    duration: PropTypes.string,
  }),
  innerRef: PropTypes.func,
  readOnly: PropTypes.bool,
  setValidity: PropTypes.func,
  shouldAssignToday: PropTypes.bool,
};

export default NonPatientActivitySchedule;
